import React from 'react';
import { CheckCircle, Calendar, Package, X, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import type { Reservation } from '../../types';

interface B2BReservationConfirmationProps {
  reservation: Reservation;
  pickupDate: string;
  onClose: () => void;
  bgError?: string | null;
}

export const B2BReservationConfirmation: React.FC<B2BReservationConfirmationProps> = ({
  reservation,
  pickupDate,
  onClose,
  bgError,
}) => {
  const formattedPickup = new Date(pickupDate).toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Reservation Confirmed
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your reservation has been submitted successfully.
          </p>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 text-left mb-6">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reservation Number</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{reservation.reservation_number}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Scheduled Pickup</p>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{formattedPickup}</p>
              </div>
            </div>
          </div>

          {bgError ? (
            <div className="flex items-start gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4 text-left">
              <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-800 dark:text-yellow-200">{bgError}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              A confirmation email has been sent. You will be notified when your order is ready for pickup.
            </p>
          )}

          <Button onClick={onClose} className="w-full">
            Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
};
